export const ROLES = {
  SUPER_ADMIN: "super_admin",
  COMPANY_ADMIN: "company_admin",
  HR_MANAGER: "hr_manager",
  TEAM_MANAGER: "team_manager",
  EMPLOYEE: "employee",
  ACCOUNTANT: "accountant",
  RECRUITER: "recruiter",
  LEGACY_ADMIN: "admin",
  MASTER_ADMIN: "master_admin",
};

const ROLE_ALIASES = {
  "super admin": ROLES.SUPER_ADMIN,
  superadmin: ROLES.SUPER_ADMIN,
  "company admin": ROLES.COMPANY_ADMIN,
  companyadmin: ROLES.COMPANY_ADMIN,
  admin: ROLES.LEGACY_ADMIN,
  "hr manager": ROLES.HR_MANAGER,
  hrmanager: ROLES.HR_MANAGER,
  "team manager": ROLES.TEAM_MANAGER,
  teammanager: ROLES.TEAM_MANAGER,
  accountant: ROLES.ACCOUNTANT,
  recruiter: ROLES.RECRUITER,
  employee: ROLES.EMPLOYEE,
  "master admin": ROLES.MASTER_ADMIN,
  masteradmin: ROLES.MASTER_ADMIN,
};

const normalizeKey = (value) => String(value || "").trim().toLowerCase().replace(/[_-]+/g, " ");

export const normalizeRole = (value) => {
  const normalized = normalizeKey(value);
  const mapped = ROLE_ALIASES[normalized] || ROLE_ALIASES[normalized.replace(/\s+/g, "")];
  return mapped || ROLES.EMPLOYEE;
};

export const isPrivilegedRole = (role) => {
  const normalized = normalizeRole(role);
  return normalized === ROLES.MASTER_ADMIN || normalized === ROLES.SUPER_ADMIN;
};

export const ALL_MODULES = {
  DASHBOARD: "dashboard",
  EMPLOYEES: "employees",
  ATTENDANCE: "attendance",
  LEAVE: "leave",
  PAYROLL: "payroll",
  EXPENSES: "expenses",
  DOCUMENTS: "documents",
  LMS: "lms",
  RECRUITMENT: "recruitment",
  TASKS: "tasks",
  PERFORMANCE: "performance",
  AI_HR_ASSISTANT: "ai_hr_assistant",
  NOTIFICATIONS: "notifications",
  INTEGRATIONS: "integrations",
  SECURITY_CENTER: "security_center",
  MOBILE_HUB: "mobile_hub",
  HR_ANALYTICS: "hr_analytics",
  FOUNDER_DASHBOARD: "founder_dashboard",
  TRACKING: "tracking",
  SYSTEM_MONITOR: "system_monitor",
  TRACKING_ALERTS: "tracking_alerts",
  ACTIVITY_LOGS: "activity_logs",
  CELEBRATIONS: "celebrations",
  AI_MONITOR: "ai_monitor",
  WELLNESS: "wellness",
  CHAT: "chat",
  SUPPORT: "support",
  ASSETS: "assets",
  HOLIDAYS: "holidays",
  SETTINGS: "settings",
  PROFILE: "profile",
  MEETINGS: "meetings",
  CALENDAR: "calendar",
};

const ALL_ACTIONS = [
  "view",
  "create",
  "edit",
  "delete",
  "approve",
  "export",
  "manage_users",
  "manage_billing",
  "view_audit_logs",
];

const buildAdminDefaults = () =>
  Object.values(ALL_MODULES).reduce((acc, key) => {
    acc[key] = [...ALL_ACTIONS];
    return acc;
  }, {});

export const ROLE_PERMISSIONS = {
  [ROLES.MASTER_ADMIN]: buildAdminDefaults(),
  [ROLES.SUPER_ADMIN]: buildAdminDefaults(),
  [ROLES.LEGACY_ADMIN]: buildAdminDefaults(),
  [ROLES.COMPANY_ADMIN]: {
    ...buildAdminDefaults(),
  },
  [ROLES.HR_MANAGER]: {
    [ALL_MODULES.DASHBOARD]: ["view", "export"],
    [ALL_MODULES.EMPLOYEES]: ["view", "create", "edit"],
    [ALL_MODULES.ATTENDANCE]: ["view", "edit", "export"],
    [ALL_MODULES.LEAVE]: ["view", "approve", "edit"],
    [ALL_MODULES.DOCUMENTS]: ["view", "create", "edit", "export"],
    [ALL_MODULES.LMS]: ["view", "create", "edit"],
    [ALL_MODULES.RECRUITMENT]: ["view", "create", "edit"],
    [ALL_MODULES.TASKS]: ["view", "create", "edit", "approve"],
    [ALL_MODULES.PERFORMANCE]: ["view", "create", "edit"],
    [ALL_MODULES.AI_HR_ASSISTANT]: ["view", "create", "edit"],
    [ALL_MODULES.NOTIFICATIONS]: ["view", "create", "edit"],
    [ALL_MODULES.INTEGRATIONS]: ["view", "create", "edit"],
    [ALL_MODULES.SECURITY_CENTER]: ["view", "create", "edit", "view_audit_logs"],
    [ALL_MODULES.HR_ANALYTICS]: ["view", "export"],
    [ALL_MODULES.CELEBRATIONS]: ["view", "create", "edit"],
    [ALL_MODULES.CHAT]: ["view", "create"],
    [ALL_MODULES.SUPPORT]: ["view", "create", "edit"],
    [ALL_MODULES.HOLIDAYS]: ["view", "create", "edit"],
    [ALL_MODULES.SETTINGS]: ["view"],
    [ALL_MODULES.ACTIVITY_LOGS]: ["view", "view_audit_logs"],
  },
  [ROLES.TEAM_MANAGER]: {
    [ALL_MODULES.DASHBOARD]: ["view"],
    [ALL_MODULES.ATTENDANCE]: ["view"],
    [ALL_MODULES.LEAVE]: ["view", "approve"],
    [ALL_MODULES.DOCUMENTS]: ["view"],
    [ALL_MODULES.TASKS]: ["view", "create", "edit", "approve"],
    [ALL_MODULES.PERFORMANCE]: ["view", "create", "edit"],
    [ALL_MODULES.AI_HR_ASSISTANT]: ["view"],
    [ALL_MODULES.NOTIFICATIONS]: ["view", "create"],
    [ALL_MODULES.LMS]: ["view"],
    [ALL_MODULES.HR_ANALYTICS]: ["view"],
    [ALL_MODULES.CHAT]: ["view", "create"],
    [ALL_MODULES.SUPPORT]: ["view", "create"],
    [ALL_MODULES.MEETINGS]: ["view", "create"],
    [ALL_MODULES.CALENDAR]: ["view"],
    [ALL_MODULES.PROFILE]: ["view", "edit"],
  },
  [ROLES.ACCOUNTANT]: {
    [ALL_MODULES.DASHBOARD]: ["view"],
    [ALL_MODULES.PAYROLL]: ["view", "create", "edit", "export"],
    [ALL_MODULES.EXPENSES]: ["view", "create", "edit", "approve", "export"],
    [ALL_MODULES.DOCUMENTS]: ["view", "export"],
    [ALL_MODULES.HR_ANALYTICS]: ["view", "export"],
    [ALL_MODULES.NOTIFICATIONS]: ["view", "create", "edit", "export"],
    [ALL_MODULES.ATTENDANCE]: ["view", "export"],
    [ALL_MODULES.CHAT]: ["view"],
    [ALL_MODULES.SUPPORT]: ["view", "create"],
    [ALL_MODULES.SETTINGS]: ["view"],
    [ALL_MODULES.ACTIVITY_LOGS]: ["view", "view_audit_logs"],
  },
  [ROLES.RECRUITER]: {
    [ALL_MODULES.DASHBOARD]: ["view"],
    [ALL_MODULES.RECRUITMENT]: ["view", "create", "edit", "export"],
    [ALL_MODULES.AI_HR_ASSISTANT]: ["view"],
    [ALL_MODULES.NOTIFICATIONS]: ["view", "create"],
    [ALL_MODULES.LMS]: ["view"],
    [ALL_MODULES.EMPLOYEES]: ["view"],
    [ALL_MODULES.DOCUMENTS]: ["view", "create"],
    [ALL_MODULES.CHAT]: ["view", "create"],
    [ALL_MODULES.SUPPORT]: ["view", "create"],
    [ALL_MODULES.SETTINGS]: ["view"],
  },
  [ROLES.EMPLOYEE]: {
    [ALL_MODULES.DASHBOARD]: ["view"],
    [ALL_MODULES.PROFILE]: ["view", "edit"],
    [ALL_MODULES.ATTENDANCE]: ["view", "create", "edit"],
    [ALL_MODULES.LEAVE]: ["view", "create"],
    [ALL_MODULES.ASSETS]: ["view"],
    [ALL_MODULES.PAYROLL]: ["view"],
    [ALL_MODULES.DOCUMENTS]: ["view", "export"],
    [ALL_MODULES.EXPENSES]: ["view", "create"],
    [ALL_MODULES.TASKS]: ["view", "edit"],
    [ALL_MODULES.AI_HR_ASSISTANT]: ["view"],
    [ALL_MODULES.MOBILE_HUB]: ["view"],
    [ALL_MODULES.LMS]: ["view"],
    [ALL_MODULES.CALENDAR]: ["view"],
    [ALL_MODULES.CHAT]: ["view", "create"],
    [ALL_MODULES.MEETINGS]: ["view"],
    [ALL_MODULES.SUPPORT]: ["view", "create"],
    [ALL_MODULES.CELEBRATIONS]: ["view", "create"],
    [ALL_MODULES.WELLNESS]: ["view"],
    [ALL_MODULES.AI_MONITOR]: ["view"],
    [ALL_MODULES.SETTINGS]: ["view", "edit"],
  },
};

const getRolePermissions = (role) => ROLE_PERMISSIONS[normalizeRole(role)] || {};

export const hasModuleAccess = (role, moduleKey) =>
  Boolean(getRolePermissions(role)?.[moduleKey]?.length);

export const hasActionPermission = (role, moduleKey, action = "view") =>
  Boolean(getRolePermissions(role)?.[moduleKey]?.includes(action));

export const canAccessAnyRole = (userRole, allowedRoles = []) => {
  const normalizedUserRole = normalizeRole(userRole);
  const set = new Set((allowedRoles || []).map((r) => normalizeRole(r)));
  return set.has(normalizedUserRole);
};

export const getDefaultRouteForRole = (role) => {
  const normalized = normalizeRole(role);
  if (normalized === ROLES.MASTER_ADMIN) return "/master-admin";
  if (normalized === ROLES.SUPER_ADMIN) return "/master-admin";
  if (
    [
      ROLES.COMPANY_ADMIN,
      ROLES.HR_MANAGER,
      ROLES.TEAM_MANAGER,
      ROLES.ACCOUNTANT,
      ROLES.RECRUITER,
      ROLES.LEGACY_ADMIN,
    ].includes(normalized)
  ) {
    return "/admin";
  }
  return "/employee";
};
